import React, { useState, useEffect, useRef } from 'react';
import pluralize from 'pluralize';
import { PROFESSIONS, fetchHeadlineTrades, FALLBACK_TRADES, FALLBACK_AFFINITY, PlatformAffinityStrip, PLATFORM_META, UPCOMING_PLATFORM_META } from './Home';
import { PLATFORM_PLAYBOOK_INFO, PROFILE_WINS, CLICK_TO_CUSTOMER, REVIEW_ENGINE, METRICS } from 'shared/playbookReference';

/*
 * HomeV2 — the "profession-playbook" front door (the A/B "B").
 * Flow: two doors -> smart profession/URL input -> instant per-profession
 * "playbook" (where/when/top idea) -> a 90%-built post whose "finish & publish"
 * = signup. See the project_profession_playbook_funnel memory for the full
 * concept + the real architecture (cached profession_playbooks, signup
 * rearrange — both on the auth/backend track).
 *
 * The playbook is REAL, server-generated data: /api/capture-business resolves
 * the trade and returns a cached-or-AI-generated playbook (the profession_playbook
 * table). There is NO hardcoded playbook fallback — if the server can't build
 * one we say so honestly rather than show invented data. Now the PRIMARY home
 * page at / (App.js); the previous home page moved to /v2.
 */

const BRAND = '#00aef7';

/* The playbook now comes entirely from the server (captured.playbook, generated
   + cached in profession_playbook). The old hardcoded PLAYBOOKS / FALLBACK /
   normalize() sample data was removed — nothing is ever read from fake data. */

const DOORS = [
  { id: 'new', title: 'My business is new to social media and I need help to get started', sub: 'I barely post — show me a better way to do it' },
  { id: 'ideas', title: "I post for my business, but I'm out of time, patience and ideas", sub: 'Give me ideas that actually work' },
];


function DoorIcon({ id }) {
  if (id === 'new') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7" aria-hidden="true">
        <path d="M12 22V11" />
        <path d="M12 12C12 8 9 6 4 6c0 5 3 7 8 6Z" />
        <path d="M12 13c0-3 3-5 8-5 0 4-3 6-8 5Z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7" aria-hidden="true">
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M12 2a6 6 0 0 0-4 10.5c1 1 1.5 2 1.5 3.5h5c0-1.5.5-2.5 1.5-3.5A6 6 0 0 0 12 2Z" />
    </svg>
  );
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Time-slot colors: slot 1 (morning) amber, slot 2 (evening) violet.
const SLICE_COLORS = [
  { bg: '#FAC775', fg: '#412402' },
  { bg: '#CECBF6', fg: '#26215C' },
];

/* PLATFORM_PLAYBOOK_INFO + the static reference content (PROFILE_WINS,
   CLICK_TO_CUSTOMER, REVIEW_ENGINE, METRICS) now live in the shared, isomorphic
   shared/playbookReference module so the email template renders the SAME source
   of truth. See that file for the accuracy/hedging notes. */

/* One ranked, full-color brand badge for the "where to post" play. Reuses the
   brand icon set from Home.jsx (PLATFORM_META / UPCOMING_PLATFORM_META). */
function PlatformBadge({ pkey, rank }) {
  const meta = PLATFORM_META[pkey] || UPCOMING_PLATFORM_META[pkey];
  if (!meta) return null;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative w-14 h-14 rounded-2xl flex items-center justify-center text-white" style={{ background: meta.bg }}>
        <span style={{ transform: 'scale(1.85)' }}>{meta.icon}</span>
        {rank ? (
          <span className="absolute -top-2 -left-2 w-[22px] h-[22px] rounded-full text-white text-[11px] font-bold flex items-center justify-center border-2 border-white" style={{ background: BRAND }}>{rank}</span>
        ) : null}
      </div>
      <span className="text-xs text-gray-500">{meta.label}</span>
    </div>
  );
}

/* The "when to post" calendar: a generic month grid with the recommended weekday
   columns filled in brand color and dotted with each day's time slots. */
function PlaybookCalendar({ days, slices }) {
  const recCols = new Set(
    (days || [])
      .map((d) => WEEKDAYS.findIndex((w) => w.toLowerCase() === String(d).toLowerCase().slice(0, 3)))
      .filter((i) => i >= 0)
  );
  const offset = 2; // generic month that starts on a Tuesday
  const cells = [];
  for (let i = 0; i < 35; i++) {
    const col = i % 7;
    const day = i - offset + 1;
    if (day < 1 || day > 31) { cells.push(<div key={i} className="aspect-square" />); continue; }
    if (recCols.has(col)) {
      cells.push(
        <div key={i} className="aspect-square flex flex-col items-center justify-center rounded-md text-xs font-semibold text-white" style={{ background: BRAND }}>
          {day}
          <span className="flex gap-0.5 mt-0.5">
            {(slices || []).slice(0, 2).map((s, k) => (
              <i key={k} className="inline-block w-1 h-1 rounded-full" style={{ background: (SLICE_COLORS[k] || SLICE_COLORS[0]).bg }} />
            ))}
          </span>
        </div>
      );
    } else {
      cells.push(<div key={i} className="aspect-square flex items-center justify-center rounded-md text-xs text-gray-400 bg-gray-50">{day}</div>);
    }
  }
  return (
    <div className="max-w-[340px]">
      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((d, i) => <div key={`h${i}`} className="text-center text-[11px] text-gray-400 py-0.5">{d[0]}</div>)}
        {cells}
      </div>
    </div>
  );
}

/* A handwritten "coach's note" in the Caveat font — used for the margin notes
   and any extra cursive lines. A slight rotation makes it read like jotting. */
function CoachNote({ children, rotate = '-1.5deg', className = '' }) {
  return (
    <p className={`text-[21px] leading-tight text-gray-500 ${className}`} style={{ fontFamily: "'Caveat', cursive", fontWeight: 600, transform: `rotate(${rotate})` }}>
      {children}
    </p>
  );
}

/* Numbered "play" wrapper — the dotted drive-rail threads the sequence together.
   `note` renders as a handwritten "coach's note" in the right margin (desktop)
   or below the content (mobile). */
function Play({ n, eyebrow, title, last, note, noteExtra, children }) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className="w-9 h-9 rounded-full text-white text-[15px] font-bold flex items-center justify-center flex-shrink-0" style={{ background: BRAND }}>{n}</div>
        {!last && <div className="w-0 flex-1 mt-1.5" style={{ borderLeft: '2px dashed rgba(0,174,247,0.35)' }} />}
      </div>
      <div className="flex-1 min-w-0 pb-8 lg:flex lg:items-start lg:gap-8">
        <div className="flex-1 min-w-0 lg:max-w-[460px]">
          <div className="text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: '#0784b8' }}>{eyebrow}</div>
          <h3 className="text-lg font-extrabold text-gray-900 leading-tight mb-2">{title}</h3>
          {children}
        </div>
        {(note || noteExtra) && (
          <div className="mt-5 lg:mt-7 lg:w-72 flex-shrink-0">
            {note && <CoachNote rotate={n % 2 === 0 ? '1deg' : '-1.5deg'}>{note}</CoachNote>}
            {noteExtra}
          </div>
        )}
      </div>
    </div>
  );
}

/* Play 1 — where to post: ranked color badges + a muted "coming soon" row. */
function WhereBody({ pb, trade }) {
  const detail = Array.isArray(pb.platformDetail) ? pb.platformDetail.filter(Boolean) : [];
  const supported = detail.filter((p) => PLATFORM_META[p.key]);
  const badges = (supported.length ? supported : detail).slice(0, 6);
  const soon = (supported.length ? detail.filter((p) => !PLATFORM_META[p.key] && UPCOMING_PLATFORM_META[p.key]) : []).slice(0, 4);
  if (badges.length === 0) {
    return <p className="text-sm text-gray-500">{(pb.platforms || []).join(', ') || '—'}</p>;
  }
  return (
    <>
      <div className="flex gap-8 flex-wrap">
        {badges.map((p, i) => <PlatformBadge key={p.key} pkey={p.key} rank={i + 1} />)}
      </div>
      {soon.length > 0 && (
        <div className="mt-3 text-xs text-gray-400">
          Strong for {trade}, coming to eHandshake soon:{' '}
          {soon.map((p) => {
            const m = UPCOMING_PLATFORM_META[p.key];
            return (
              <span key={p.key} className="inline-flex items-center gap-2 text-xs rounded-full bg-gray-100 text-gray-600 pl-1 pr-2.5 py-1 ml-1 mb-1 align-middle">
                <span className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-md text-white" style={{ background: m.bg }}>{m.icon}</span>
                {m.label}
              </span>
            );
          })}
        </div>
      )}
    </>
  );
}

/* Play 2 — when to post: two time-slot chips + the month calendar. Falls back to
   the best_time string for playbooks generated before posting_schedule existed. */
function WhenBody({ pb, trade }) {
  const sched = pb.postingSchedule;
  const slices = (sched && Array.isArray(sched.slices) ? sched.slices : []).slice(0, 2);
  const hasCal = sched && Array.isArray(sched.days) && sched.days.length > 0;
  return (
    <>
      {slices.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-3">
          {slices.map((s, i) => {
            const c = SLICE_COLORS[i] || SLICE_COLORS[0];
            return (
              <span key={i} className="inline-flex items-center text-[13px] px-3 py-1.5 rounded-full font-semibold" style={{ background: c.bg, color: c.fg }}>
                {s.label}{s.window ? ` · ${s.window}` : ''}
              </span>
            );
          })}
        </div>
      )}
      {hasCal ? (
        <>
          <PlaybookCalendar days={sched.days} slices={slices} />
          <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-[3px]" style={{ background: BRAND }} />post day</span>
            {pb.cadence && <span>· {pb.cadence}</span>}
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-700">{pb.bestTime || '—'}{pb.cadence ? ` · ${pb.cadence}` : ''}</p>
      )}
      <p className="mt-2 text-xs text-gray-400 italic">
        Based on general best practices for {trade} — a solid starting point to fine-tune as you learn when your audience is most engaged.
      </p>
    </>
  );
}

/* Play 3 — what to post: focused "content idea" cards in the Design Center
   inspiration style — the post TOPIC + a sample caption (what to say), never the
   work. Card #1 is the playbook's pre-baked seed_idea (instant); + generates more
   (up to 3) via /api/playbook-idea, with left/right nav. "Use this idea" is a
   non-clickable preview of the card they'll get after signup. */
function WhatBody({ pb, trade }) {
  const MAX = 3;
  const [ideas, setIdeas] = useState(pb.seedIdea && pb.seedIdea.title ? [pb.seedIdea] : []);
  const [cur, setCur] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const canGen = ideas.length < MAX && !loading;

  const generate = async () => {
    if (!canGen) return;
    setErr(''); setLoading(true);
    try {
      const res = await fetch('/api/playbook-idea', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profession: trade, avoid: ideas.map((i) => i.title) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok || !data.idea || !data.idea.title) {
        setErr(data.message || 'Could not generate an idea. Try again.');
      } else {
        setIdeas((prev) => { const next = [...prev, data.idea]; setCur(next.length - 1); return next; });
      }
    } catch (_) {
      setErr('Could not generate an idea. Try again.');
    }
    setLoading(false);
  };

  const idea = ideas[cur];
  return (
    <div className="max-w-md">
      {idea && (
        <div className="rounded-2xl p-5" style={{ background: '#f5fbff', boxShadow: 'inset 0 0 0 1px rgba(0,174,247,0.18)' }}>
          <h4 className="text-lg font-bold text-gray-900 leading-snug">{idea.title}</h4>
          {idea.caption && (
            <p className="mt-2 text-[15px] italic text-gray-500 leading-relaxed">&ldquo;{idea.caption}&rdquo;</p>
          )}
          <div className="mt-3 text-[15px] font-bold cursor-default" style={{ color: BRAND }} title="Available when you sign up">
            Use this idea →
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={generate}
          disabled={!canGen}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white transition hover:scale-[1.02] disabled:opacity-30 disabled:hover:scale-100"
          style={{ background: BRAND }}
        >
          {loading
            ? <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
            : <span className="text-base leading-none">+</span>}
          {idea ? 'Another idea' : 'Generate an idea'}
        </button>

        {ideas.length > 1 && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setCur((c) => (c - 1 + ideas.length) % ideas.length)} aria-label="Previous idea" className="text-gray-400 hover:text-gray-700 text-xl leading-none">‹</button>
            <div className="flex gap-1">
              {ideas.map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === cur ? '' : 'opacity-30'}`} style={{ background: BRAND }} />)}
            </div>
            <button type="button" onClick={() => setCur((c) => (c + 1) % ideas.length)} aria-label="Next idea" className="text-gray-400 hover:text-gray-700 text-xl leading-none">›</button>
          </div>
        )}

        <span className="text-xs text-gray-400">
          {ideas.length >= MAX ? 'Sign up to generate unlimited ideas' : `tuned to ${trade}`}
        </span>
      </div>
      {err && <div className="mt-2 text-xs text-red-600">{err}</div>}
    </div>
  );
}

// Platforms that have a composed hero video in S3 (videos/hero/{key}.mp4).
// Matches the V1 home carousel's set; "coming soon" platforms have no video.
const VIDEO_PLATFORMS = new Set(['facebook', 'instagram', 'tiktok', 'youtube', 'linkedin', 'x', 'whatsapp']);
const PLATFORM_VIDEO_RATE = 0.55; // slow playback so the post + landing page are readable (matches V1)

/* Rotates the profession's platform hero videos with a crossfade — the same
   composed post + landing-page clips the V1 home hero uses, filtered to the
   platforms THIS trade posts to (e.g. plumber → facebook/instagram/tiktok/
   youtube). All clips are stacked; only the current one plays, and its onEnded
   advances to the next (looping). */
function PlatformVideoRotator({ platformKeys }) {
  const srcs = (platformKeys || [])
    .filter((k) => VIDEO_PLATFORMS.has(k))
    .map((k) => `https://ehandshake.com/videos/hero/${k}.mp4`);
  const refs = useRef([]);
  const [cur, setCur] = useState(0);

  useEffect(() => { setCur(0); }, [srcs.join('|')]);

  useEffect(() => {
    refs.current.forEach((v, i) => {
      if (!v) return;
      if (i === cur) {
        try { v.currentTime = 0; } catch (_) { /* not ready yet */ }
        v.playbackRate = PLATFORM_VIDEO_RATE;
        v.play().catch(() => {});
      } else {
        v.pause();
      }
    });
  }, [cur, srcs.length]);

  if (srcs.length === 0) return null;
  const advance = () => setCur((c) => (c + 1) % srcs.length);

  return (
    <div className="relative aspect-video w-full max-w-md rounded-2xl overflow-hidden bg-gray-50">
      {srcs.map((src, i) => (
        <video
          key={src}
          ref={(el) => { refs.current[i] = el; }}
          src={src}
          muted
          playsInline
          preload="auto"
          onEnded={advance}
          onLoadedData={(e) => {
            e.currentTarget.playbackRate = PLATFORM_VIDEO_RATE;
            if (i === cur) e.currentTarget.play().catch(() => {});
          }}
          disablePictureInPicture
          onContextMenu={(e) => e.preventDefault()}
          aria-hidden={i !== cur}
          className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-1000 ${i === cur ? 'opacity-100' : 'opacity-0'}`}
        />
      ))}
    </div>
  );
}

/* The HubSpot landing-page stat, compact for the margin column. Real, cited
   stat (mirrors LandingPageStatCallout on the V1 home page) — answers the
   handwritten "why is a landing page important?" note above it. */
function LandingPageStat() {
  return (
    <div className="mt-4 bg-[#f5fbff] border border-[#00aef7]/20 rounded-2xl px-4 py-3">
      <p className="text-gray-700 text-sm leading-relaxed">
        <span className="font-bold" style={{ color: BRAND }}>Companies with 10+ landing pages generate 12× more leads</span>{' '}
        than those with fewer than 5.{' '}
        <span className="italic text-gray-500">(<a href="https://hubspot-state-of-marketing-2026.replit.app/" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#00aef7] transition-colors">HubSpot</a>)</span>
      </p>
    </div>
  );
}

/* Conductor "educational content → 131% more likely to buy" stat, compact for
   the margin. Real, cited study (verified at the Conductor source) — backs up
   the "educate them, don't sell to them" note above it in Play 3. */
function EducationalContentStat() {
  return (
    <div className="mt-4 bg-[#f5fbff] border border-[#00aef7]/20 rounded-2xl px-4 py-3">
      <p className="text-gray-700 text-sm leading-relaxed">
        <span className="font-bold" style={{ color: BRAND }}>Consumers are 131% more likely to buy</span>{' '}
        from a brand after reading its helpful, educational content.{' '}
        <span className="italic text-gray-500">(<a href="https://www.conductor.com/academy/winning-customers-educational-content/" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#00aef7] transition-colors">Conductor</a>)</span>
      </p>
    </div>
  );
}

/* Harvard Business Review omnichannel finding, compact for the margin. Real,
   cited study (46,000 shoppers) — supports Play 5's "share everywhere you have a
   presence": customers who engage across channels are worth more. HBR is a
   research publication, not a competitor. */
function OmnichannelStat() {
  return (
    <div className="mt-4 bg-[#f5fbff] border border-[#00aef7]/20 rounded-2xl px-4 py-3">
      <p className="text-gray-700 text-sm leading-relaxed">
        <span className="font-bold" style={{ color: BRAND }}>Customers who engage across channels spend more</span>{' '}
        — 10% more online than single-channel shoppers, in a study of 46,000.{' '}
        <span className="italic text-gray-500">(<a href="https://hbr.org/2017/01/a-study-of-46000-shoppers-shows-that-omnichannel-retailing-works" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#00aef7] transition-colors">Harvard Business Review</a>)</span>
      </p>
    </div>
  );
}

/* Sprout Social 2026 "best times to post" stat, compact for the margin. Verified
   at the Sprout source — nearly 2 billion engagements analyzed. Backs Play 2's
   calendar (the per-trade days/times refine this cross-platform benchmark).
   NOTE: Sprout is a competitor — Brett wants a non-competitor source eventually. */
function PostTimingStat() {
  return (
    <div className="mt-4 bg-[#f5fbff] border border-[#00aef7]/20 rounded-2xl px-4 py-3">
      <p className="text-gray-700 text-sm leading-relaxed">
        <span className="font-bold" style={{ color: BRAND }}>Engagement peaks Tuesdays and Wednesdays, 11am–6pm</span>{' '}
        — across nearly 2 billion engagements analyzed.{' '}
        <span className="italic text-gray-500">(<a href="https://sproutsocial.com/insights/best-times-to-post-on-social-media/" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#00aef7] transition-colors">Sprout Social</a>)</span>
      </p>
    </div>
  );
}

/* Play 4 — your first post: the profession's platform videos in rotation (the
   "one design, every platform" story), replacing the old single draft card. */
function DraftBody({ pb }) {
  const keys = (Array.isArray(pb.platformDetail) ? pb.platformDetail : [])
    .filter((p) => p && PLATFORM_META[p.key])
    .map((p) => p.key);
  if (keys.length === 0) {
    return <p className="text-sm text-gray-500">We'll set up your first post the moment you start.</p>;
  }
  return <PlatformVideoRotator platformKeys={keys} />;
}

/* Play 5 — share it (Sharing Center). First of the product-walkthrough plays;
   more (post, design center, ... teams) get appended in PlaybookPlays as we go. */
function ShareBody() {
  return (
    <img
      src="/images/team/analytics-dashboardFB.png"
      alt="Your post shared across every social platform"
      loading="lazy"
      className="w-full max-w-md rounded-2xl"
    />
  );
}

/* Play 6 — amplify it (Teams / Drip Networking, the differentiator): the team-
   amplification graphic. Placeholder image (Panel3Teams.webp) until Brett
   produces the final graphic. */
function TeamBody() {
  return (
    <img
      src="/images/Teams/Panel3Teams.webp"
      alt="A team of local businesses amplifying each other's posts"
      loading="lazy"
      className="w-full max-w-md rounded-2xl"
    />
  );
}

/* eHandshake's own team-amplification "citation" — no external source, so it's
   marked (eHandshake). Same box style as the stat citations; sits in Play 6's
   right margin like the others. */
function TeamCitation() {
  return (
    <div className="mt-4 bg-[#f5fbff] border border-[#00aef7]/20 rounded-2xl px-4 py-3">
      <p className="text-gray-700 text-sm leading-relaxed">
        <span className="font-bold" style={{ color: BRAND }}>Join a team of non-competing, local businesses that boost each other</span>{' '}
        — you post once, and your teammates share, like, and comment, putting it in front of their audiences too.{' '}
        <span className="italic text-gray-500">(eHandshake)</span>
      </p>
    </div>
  );
}

/* ── Playbook reference appendix ──────────────────────────────────────────────
   Below the numbered "plays" (the do-this-now drive), a reference zone: the why
   behind the plays + how the platforms actually work. Styled as a report
   appendix (cards, not the numbered rail). Per path: the "ideas" door also gets
   a Where/When card here (it's demoted out of the main flow); the "new" door
   gets it as plays 1-2 so it isn't repeated. */
function RefCard({ eyebrow = 'reference', title, subtitle, children }) {
  return (
    <div className="rounded-2xl ring-1 ring-gray-200 bg-white p-5 md:p-6">
      <div className="text-[11px] font-bold uppercase tracking-[0.12em] mb-1" style={{ color: '#0784b8' }}>{eyebrow}</div>
      <h4 className="text-lg font-extrabold text-gray-900 leading-tight">{title}</h4>
      {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </div>
  );
}

/* The "how each platform treats your post" cards — organic reach, post lifespan,
   what gets it shown more, one tip. Only the profession's recommended platforms
   we have data for. Numbers are hedged industry averages (see PLATFORM_PLAYBOOK_INFO). */
function AlgoStat({ label, children }) {
  return (
    <div className="mt-2.5">
      <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{label}</span>
      <div className="text-[13px] text-gray-700 leading-snug">{children}</div>
    </div>
  );
}

function PlatformAlgoRef({ detail }) {
  const rows = (detail || [])
    .map((p) => ({ key: p.key, info: PLATFORM_PLAYBOOK_INFO[p.key], meta: PLATFORM_META[p.key] || UPCOMING_PLATFORM_META[p.key] }))
    .filter((r) => r.info && r.meta);
  if (rows.length === 0) return null;
  return (
    <RefCard
      title="How each platform treats your post"
      subtitle="Where your post actually goes, and what makes more people see it. Approximate industry averages for business accounts — yours will vary with your audience and content."
    >
      <div className="grid sm:grid-cols-2 gap-3">
        {rows.map((r) => (
          <div key={r.key} className="rounded-xl bg-gray-50 ring-1 ring-gray-100 p-4">
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-lg flex items-center justify-center text-white flex-shrink-0" style={{ background: r.meta.bg }}>
                <span style={{ transform: 'scale(1.2)' }}>{r.meta.icon}</span>
              </span>
              <span className="font-bold text-gray-900">{r.meta.label}</span>
            </div>
            <AlgoStat label="Seen by">{r.info.reach}<span className="text-gray-400"> — {r.info.reachNote}</span></AlgoStat>
            <AlgoStat label="Stays alive">{r.info.life}</AlgoStat>
            <AlgoStat label="Shown to more when">{r.info.boost}</AlgoStat>
            <AlgoStat label="Post how often">{r.info.cadence}</AlgoStat>
            <AlgoStat label="Get found by">{r.info.discovery}</AlgoStat>
            <div className="mt-3 text-[13px] rounded-lg px-3 py-2" style={{ background: 'rgba(0,174,247,0.08)' }}>
              <span className="font-semibold" style={{ color: '#0784b8' }}>Tip:</span> <span className="text-gray-700">{r.info.tip}</span>
            </div>
          </div>
        ))}
      </div>
    </RefCard>
  );
}

/* Profession-specific content themes (pb.contentThemes) as a "rotate these" mix. */
function ContentMixRef({ pb, trade }) {
  const themes = (Array.isArray(pb.contentThemes) ? pb.contentThemes : []).filter(Boolean);
  if (themes.length === 0) return null;
  return (
    <RefCard title="Your content mix" subtitle={`Rotate these so every post isn't the same pitch — picked for ${trade}.`}>
      <div className="flex flex-wrap gap-2">
        {themes.map((t, i) => (
          <span key={i} className="inline-flex items-center text-[13px] px-3 py-1.5 rounded-full font-medium text-gray-700 bg-gray-100">{t}</span>
        ))}
      </div>
    </RefCard>
  );
}

/* What to actually watch — real-interest signals over vanity metrics. Static. */
function MetricsRef() {
  return (
    <RefCard title="Metrics that matter" subtitle="These signal real interest. Don't chase the vanity ones.">
      <ul className="space-y-2">
        {METRICS.good.map(([k, v]) => (
          <li key={k} className="flex gap-2.5 text-sm">
            <span className="mt-0.5 font-bold" style={{ color: BRAND }}>✓</span>
            <span><span className="font-semibold text-gray-900">{k}</span> <span className="text-gray-500">— {v}</span></span>
          </li>
        ))}
        <li className="flex gap-2.5 text-sm">
          <span className="mt-0.5 text-gray-300">○</span>
          <span className="text-gray-400">{METRICS.vanity}</span>
        </li>
      </ul>
    </RefCard>
  );
}

/* Profile / local-SEO quick wins — the checklist that turns a profile visit into
   a contact. Static, from shared/playbookReference (PROFILE_WINS). */
function ProfileWinsRef() {
  return (
    <RefCard title="Quick profile wins" subtitle="Small fixes that make a new visitor trust you — and reach out.">
      <ul className="space-y-2.5">
        {PROFILE_WINS.map(([k, v]) => (
          <li key={k} className="flex gap-2.5 text-sm">
            <span className="mt-0.5 font-bold" style={{ color: BRAND }}>✓</span>
            <span><span className="font-semibold text-gray-900">{k}</span> <span className="text-gray-500">— {v}</span></span>
          </li>
        ))}
      </ul>
    </RefCard>
  );
}

/* The click → customer math — cited; quietly makes the landing-page case. */
function ClickToCustomerRef() {
  return (
    <RefCard title={CLICK_TO_CUSTOMER.headline} subtitle="">
      <p className="text-sm text-gray-700 leading-relaxed -mt-2">
        {CLICK_TO_CUSTOMER.detail}{' '}
        <span className="italic text-gray-400">(<a href={CLICK_TO_CUSTOMER.href} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#00aef7] transition-colors">{CLICK_TO_CUSTOMER.source}</a>)</span>
      </p>
    </RefCard>
  );
}

/* The review engine — turn happy customers into posts. Static (REVIEW_ENGINE). */
function ReviewEngineRef() {
  return (
    <RefCard title={REVIEW_ENGINE.headline} subtitle="Your customers' words sell harder than yours — put them to work.">
      <ul className="space-y-2.5">
        {REVIEW_ENGINE.points.map((p, i) => (
          <li key={i} className="flex gap-2.5 text-sm">
            <span className="mt-0.5 font-bold" style={{ color: BRAND }}>★</span>
            <span className="text-gray-700">{p}</span>
          </li>
        ))}
      </ul>
    </RefCard>
  );
}

/* "ideas" door only: where + when, demoted out of the main flow into reference. */
function WhereWhenRef({ pb, trade }) {
  return (
    <RefCard title="Where &amp; when to post" subtitle={`You've got the ideas — here's the distribution plan for ${trade}.`}>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-2">Platforms</div>
          <WhereBody pb={pb} trade={trade} />
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-2">Best times</div>
          <WhenBody pb={pb} trade={trade} />
        </div>
      </div>
    </RefCard>
  );
}

function PlaybookReference({ pb, trade, door }) {
  const detail = (Array.isArray(pb.platformDetail) ? pb.platformDetail : []).filter(Boolean);
  return (
    <div className="mt-12 pt-8 border-t-2 border-dashed border-gray-200">
      <div className="flex items-center gap-2.5 mb-1">
        <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white flex-shrink-0" style={{ background: BRAND }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-[18px] h-[18px]" aria-hidden="true">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </svg>
        </span>
        <h3 className="text-xl md:text-2xl font-extrabold tracking-tight text-gray-900">Playbook reference</h3>
      </div>
      <p className="text-sm text-gray-500 mb-6">Keep this handy — the why behind the plays, and how the platforms actually work.</p>
      <div className="space-y-4">
        {door === 'ideas' && <WhereWhenRef pb={pb} trade={trade} />}
        <PlatformAlgoRef detail={detail} />
        <ContentMixRef pb={pb} trade={trade} />
        <ClickToCustomerRef />
        <ProfileWinsRef />
        <ReviewEngineRef />
        <MetricsRef />
      </div>
    </div>
  );
}

/* Assembles the ordered plays + the reference appendix. The per-profession data
   plays reorder by the door; plays 5-6 are the product walkthrough (share, team).
   "ideas" door: lead with the IDEA, flow through teams, demote where/when into
   the reference. "new" door: where/when lead as plays 1-2. */
function PlaybookPlays({ pb, trade, door }) {
  const where = { eyebrow: 'where to post', title: 'Where you should be posting', note: 'More platforms, more eyes on your content. Start where your trade already hangs out. New customers are in that space.', body: <WhereBody pb={pb} trade={trade} /> };
  const when = { eyebrow: 'when to post', title: 'When to post - Set a consistent schedule', note: 'Same days, same times — show up like clockwork and folks start seeing  you consistently. That is good exposure.', noteExtra: <PostTimingStat />, body: <WhenBody pb={pb} trade={trade} /> };
  const what = { eyebrow: 'what to post', title: 'What to post? - We give you the ideas - no more struggling with the "blank Page" syndrome', note: "Don't overthink it — just post what you already know. Educate them; don't sell to them.", noteExtra: <EducationalContentStat />, body: <WhatBody pb={pb} trade={trade} /> };
  const draft = { eyebrow: 'your first play', title: 'Your first post done in less than 5 minutes — to all platforms you choose', note: 'Link your own landing page, or our AI builds one — any public page works: Pinterest, YouTube, Shopify, wherever your content lives. Just make sure it relates to your post content', noteExtra: <LandingPageStat />, body: <DraftBody pb={pb} /> };

  // "ideas" door: lead with the IDEA, then first-post → share → team; where/when
  // are demoted into the reference appendix below. "new" door: where/when lead.
  const dataPlays = door === 'ideas' ? [what, draft] : [where, when, what, draft];

  const narrativePlays = [
    { eyebrow: 'share it', title: 'Share it everywhere you have a presence', note: 'Share to any social platform, WhatsApp and by email.', noteExtra: <OmnichannelStat />, body: <ShareBody /> },
    { eyebrow: 'amplify it', title: 'Your team amplifies every post you make', note: 'Your reach compounds with every teammate who joins — no extra posts, no extra work.', noteExtra: <TeamCitation />, body: <TeamBody /> },
  ];

  const plays = [...dataPlays, ...narrativePlays];
  return (
    <div className="mt-6">
      {plays.map((p, i) => (
        <Play key={i} n={i + 1} eyebrow={p.eyebrow} title={p.title} note={p.note} noteExtra={p.noteExtra} last={i === plays.length - 1}>
          {p.body}
        </Play>
      ))}
      <PlaybookReference pb={pb} trade={trade} door={door} />
    </div>
  );
}

/* Rotating profession showcase — crossfades one profession at a time every 3s.
   Pulls the FULL canonical list (~175) live from the same endpoint V1's marquee
   uses (fetchHeadlineTrades -> allNames). The static PROFESSIONS names are only
   the first-paint fallback until that resolves (or if the fetch fails). Name
   only, no platforms. */
function RotatingProfession() {
  const [names, setNames] = useState(() => PROFESSIONS.map((p) => p.name));
  const [i, setI] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchHeadlineTrades().then(({ allNames }) => {
      if (cancelled || !Array.isArray(allNames) || allNames.length === 0) return;
      setNames(allNames);
      setI(0);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setI((n) => (n + 1) % names.length), 3000);
    return () => clearInterval(id);
  }, [names.length]);

  return (
    <div className="mt-6 relative h-9" aria-live="polite" aria-label="professions we serve">
      {names.map((name, idx) => (
        <span
          key={idx}
          aria-hidden={idx !== i}
          className={`absolute inset-0 flex items-center justify-center text-2xl font-extrabold transition-opacity duration-500 ${
            idx === i ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          style={{ color: BRAND }}
        >
          {name}
        </span>
      ))}
    </div>
  );
}

/* "Where {trade} posts" rotation — V1's platform-affinity strip, reused here
   and cycled every 3s (vs 5s on V1). Seeds with the same fallbacks V1 uses,
   then swaps to live data. Shows supported platforms + the "coming soon" set. */
function PlatformRotation() {
  const [trades, setTrades] = useState(FALLBACK_TRADES);
  const [affinity, setAffinity] = useState(FALLBACK_AFFINITY);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchHeadlineTrades().then(({ trades: t, affinity: a }) => {
      if (cancelled) return;
      if (Array.isArray(t) && t.length) setTrades(t);
      if (a && typeof a === 'object' && Object.keys(a).length) setAffinity(a);
      setIdx(0);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setIdx((n) => (n + 1) % trades.length), 3000);
    return () => clearInterval(id);
  }, [trades.length]);

  const trade = trades[idx] || trades[0];
  const platforms = affinity[(trade || '').toLowerCase()] || [];

  return (
    <div className="mt-9 flex justify-center">
      <PlatformAffinityStrip trade={trade} platforms={platforms} />
      <style>{`@keyframes heroSwap{0%{opacity:0;transform:translateY(8px)}100%{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

/* Inline "email me my playbook" — a soft conversion. Reveals an email field,
   POSTs to /api/playbook-email (emails the playbook + captures the lead), then
   confirms. */
function EmailMyPlaybook({ profession, professionCanonicalId, captureToken }) {
  const [state, setState] = useState('idle'); // idle | open | sending | sent
  const [email, setEmail] = useState('');
  const [err, setErr] = useState('');

  const send = async () => {
    if (state === 'sending') return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setErr('Enter a valid email.'); return; }
    setErr(''); setState('sending');
    try {
      const res = await fetch('/api/playbook-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), profession, professionCanonicalId, captureToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) setState('sent');
      else { setErr(data.message || 'Could not send. Try again.'); setState('open'); }
    } catch (_) {
      setErr('Could not send. Try again.'); setState('open');
    }
  };

  if (state === 'sent') {
    return <div className="mt-4 text-sm text-gray-600">✓ Sent — check your inbox for your {profession} playbook.</div>;
  }
  if (state === 'idle') {
    return (
      <div className="mt-4 text-xs text-gray-400">
        not ready? <button type="button" onClick={() => setState('open')} className="font-semibold" style={{ color: BRAND }}>email me my playbook</button>
      </div>
    );
  }
  return (
    <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex items-center justify-center gap-2 flex-wrap">
      <input
        type="email"
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your email"
        className="rounded-full ring-1 ring-gray-300 focus:ring-2 focus:ring-[#00aef7] px-4 py-2 text-sm outline-none w-56"
      />
      <button type="submit" disabled={state === 'sending'} className="rounded-full px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: BRAND }}>
        {state === 'sending' ? 'Sending…' : 'Email it to me'}
      </button>
      {err && <div className="w-full text-xs text-red-600">{err}</div>}
    </form>
  );
}

/* Small colored share badge — a platform's brand icon, or custom children. */
function ShareBadge({ pkey, href, onClick, label, children }) {
  const meta = pkey ? PLATFORM_META[pkey] : null;
  const bg = meta ? meta.bg : '#64748b';
  const cls = 'w-9 h-9 rounded-full flex items-center justify-center text-white transition-transform hover:scale-110';
  const inner = meta ? <span style={{ transform: 'scale(1.15)' }}>{meta.icon}</span> : children;
  if (onClick) return <button type="button" onClick={onClick} aria-label={label} title={label} className={cls} style={{ background: bg }}>{inner}</button>;
  return <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} className={cls} style={{ background: bg }}>{inner}</a>;
}

/* Share THIS playbook via a ?trade= permalink that auto-loads the same playbook
   for whoever opens it — a viral loop. Native share sheet on mobile + copy-link
   / social / email everywhere. */
function SharePlaybook({ trade, tradePlural }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/?trade=${encodeURIComponent(trade)}`;
  const text = `I just got a free social-media game plan for ${tradePlural} — where, when & what to post. Make yours:`;
  const enc = encodeURIComponent;
  const native = (typeof navigator !== 'undefined' && navigator.share)
    ? () => navigator.share({ title: 'My eHandshake playbook', text, url }).catch(() => {})
    : null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_) { /* ignore */ }
  };

  return (
    <div className="mt-6">
      <div className="text-xs text-gray-400 mb-2">share this playbook</div>
      <div className="flex items-center justify-center gap-2 flex-wrap">
        <button type="button" onClick={copy} className="inline-flex items-center rounded-full ring-1 ring-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:ring-[#00aef7] transition">
          {copied ? 'Copied!' : 'Copy link'}
        </button>
        {native && (
          <button type="button" onClick={native} className="rounded-full px-3 py-1.5 text-sm font-semibold text-white" style={{ background: BRAND }}>Share…</button>
        )}
        <ShareBadge pkey="x" href={`https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`} label="Share on X" />
        <ShareBadge pkey="facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`} label="Share on Facebook" />
        <ShareBadge pkey="linkedin" href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`} label="Share on LinkedIn" />
        <ShareBadge pkey="whatsapp" href={`https://wa.me/?text=${enc(`${text} ${url}`)}`} label="Share on WhatsApp" />
        <ShareBadge href={`mailto:?subject=${enc(`A social media playbook for ${tradePlural}`)}&body=${enc(`${text}\n\n${url}`)}`} label="Share by email">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
        </ShareBadge>
      </div>
    </div>
  );
}

export default function HomeV2() {
  const [step, setStep] = useState('entry'); // entry | input | playbook
  const [door, setDoor] = useState(null);
  const [profession, setProfession] = useState('');
  const [website, setWebsite] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captured, setCaptured] = useState(null);

  // The playbook is the REAL server-generated one returned by the capture
  // endpoint. No hardcoded fallback — pb may be null, in which case the playbook
  // step renders an honest "building it" state instead of invented data.
  const pb = (captured && captured.playbook) || null;
  // What the playbook copy names = the server-detected / typed trade.
  const displayTrade = (captured && captured.profession) || (pb && pb.label) || 'your business';
  // Canonical names are inconsistently singular/plural — normalize so the copy
  // reads right either way: singular for the headline noun ("The Plumber
  // playbook"), plural for the subject sentences ("how Plumbers get found").
  const tradeSingular = pluralize.singular(displayTrade);
  const tradePlural = pluralize(displayTrade);

  // Load the Caveat handwriting font for the margin "coach notes" — injected
  // once on mount (scoped to this page rather than global in index.html).
  useEffect(() => {
    const id = 'eh-caveat-font';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@600&display=swap';
    document.head.appendChild(link);
  }, []);

  // Shared playbook permalink: /?trade=<profession> auto-loads that playbook for
  // whoever opens the link (the viral loop) — skips the doors/input. Runs once.
  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('trade');
    if (!t || !t.trim()) return;
    setDoor('new');
    setStep('input');
    setLoading(true);
    fetch('/api/capture-business', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profession: t.trim() }),
    })
      .then((r) => r.json().catch(() => ({})))
      .then((data) => {
        if (data && data.ok) {
          if (data.captureToken) { try { localStorage.setItem('eh_capture_token', data.captureToken); } catch (_) { /* ignore */ } }
          setCaptured(data);
          setStep('playbook');
        } else {
          setStep('entry');
        }
        setLoading(false);
      })
      .catch(() => { setLoading(false); setStep('entry'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => {
    setStep('entry'); setDoor(null); setProfession(''); setWebsite('');
    setLoading(false); setError(''); setCaptured(null);
  };
  const pickDoor = (id) => { setDoor(id); setStep('input'); };

  // Real backend call: SSRF guard + blocklist + moderation + site scan. On a
  // rejection we stay on the input step and show the (deliberately boring) reason.
  const submit = async () => {
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/capture-business', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ website: website.trim(), profession: profession.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setLoading(false);
        setError(data.message || "That doesn't look like a business. Try again.");
        return;
      }
      if (data.captureToken) {
        try { localStorage.setItem('eh_capture_token', data.captureToken); } catch (_) { /* ignore */ }
      }
      setCaptured(data);
      setLoading(false);
      setStep('playbook');
    } catch (_) {
      setLoading(false);
      setError('Something went wrong. Please try again.');
    }
  };

  return (
    <main className="flex-grow bg-white">
      <div className={`mx-auto w-[92%] ${step === 'playbook' ? 'max-w-5xl' : 'max-w-3xl'} min-h-[78vh] flex flex-col justify-center py-12 md:py-16`}>

        {step === 'entry' && (
          <div className="text-center">
            <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-gray-400 mb-3">Built for local, small businesses</div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 leading-[1.1]">
              Social media that brings in <span style={{ color: BRAND }}>customers</span> — without the work.
            </h1>
            <p className="mt-5 text-gray-500 text-lg">Which one of these sounds like you?</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4 text-left">
              {DOORS.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => pickDoor(d.id)}
                  className="group rounded-2xl ring-1 ring-gray-200 hover:ring-2 hover:ring-[#00aef7] p-6 transition-all hover:shadow-lg bg-white"
                >
                  <span style={{ color: BRAND }}><DoorIcon id={d.id} /></span>
                  <div className="mt-3 text-lg font-bold text-gray-900 leading-snug">{d.title}</div>
                  <div className="mt-1 text-sm text-gray-500 flex items-center gap-1">
                    {d.sub} <span className="transition-transform group-hover:translate-x-1">→</span>
                  </div>
                </button>
              ))}
            </div>

            <RotatingProfession />


          </div>
        )}

        {step === 'input' && (
          <div className="text-center">
            {loading ? (
              <div className="py-20 flex flex-col items-center">
                <div className="w-10 h-10 rounded-full border-[3px] border-gray-200 border-t-[#00aef7] animate-spin" />
                <p className="mt-5 text-gray-600 text-lg">
                  {website.trim() ? 'Reading your site and detecting your trade…' : 'Building your playbook…'}
                </p>
              </div>
            ) : (
              <>
                <button type="button" onClick={reset} className="text-sm text-gray-400 hover:text-gray-600 mb-6">← back</button>
                <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">Let's tailor this to your business.</h1>
                <p className="mt-3 text-gray-500">Point us at your business online and our AI does the rest — or just tell us your trade.</p>

                {error && (
                  <div className="mt-5 max-w-xl mx-auto rounded-xl bg-red-50 ring-1 ring-red-200 text-red-700 text-sm px-4 py-3">
                    {error}
                  </div>
                )}

                <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="mt-6">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-center gap-3 max-w-2xl mx-auto text-left">
                    <div className="w-full sm:flex-1">
                      <input
                        autoFocus
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        placeholder="your website or social page"
                        className="w-full rounded-xl ring-1 ring-[#00aef7]/60 focus:ring-2 focus:ring-[#00aef7] px-5 py-3.5 text-base outline-none"
                      />
                      <p className="mt-2 text-xs text-gray-400 leading-relaxed">
                        Website, TikTok, Instagram, LinkedIn, Google Business — any page that shows what you do. We scan it and do most of your setup for you.
                      </p>
                    </div>
                    <span className="hidden sm:block text-sm text-gray-400 pt-4">or</span>
                    <div className="w-full sm:flex-1">
                      <input
                        value={profession}
                        onChange={(e) => setProfession(e.target.value)}
                        placeholder="your profession"
                        className="w-full rounded-xl ring-1 ring-gray-300 focus:ring-2 focus:ring-[#00aef7] px-5 py-3.5 text-base outline-none"
                      />
                      <p className="mt-2 text-xs text-gray-400 leading-relaxed">
                        Your industry or profession — be specific about your specialty. “Criminal defense attorney,” not just “lawyer.”
                      </p>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="mt-6 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-white text-base font-semibold transition-transform hover:scale-[1.02]"
                    style={{ background: BRAND }}
                  >
                    Show me my playbook →
                  </button>
                </form>

                <PlatformRotation />
              </>
            )}
          </div>
        )}

        {step === 'playbook' && (
          <div>
            <button type="button" onClick={reset} className="text-sm text-gray-400 hover:text-gray-600 mb-5">← start over</button>

            {captured && captured.source === 'website' && (
              <div className="mb-5 rounded-xl bg-[#f0faff] ring-1 ring-[#00aef7]/25 px-5 py-4 text-left">
                {captured.businessName && (
                  <div className="text-sm text-gray-500">We read <span className="font-semibold text-gray-800">{captured.businessName}</span></div>
                )}
                <div className="mt-0.5 text-base text-gray-900">
                  Your industry:{' '}
                  <span className="font-bold" style={{ color: BRAND }}>
                    {captured.profession || "we couldn't tell — type your trade instead"}
                  </span>
                </div>
                <button type="button" onClick={reset} className="mt-1 text-xs text-gray-400 hover:text-gray-600 underline">
                  Not right? Start over
                </button>
              </div>
            )}

            <div className="flex items-center gap-3 mb-1.5">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white" style={{ background: BRAND }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6" aria-hidden="true">
                  <rect x="8" y="3" width="8" height="4" rx="1" />
                  <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                  <path d="M9 12h6M9 16h4" />
                </svg>
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: '#0784b8' }}>your game plan</div>
                <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-gray-900 leading-tight">The {tradeSingular} playbook</h2>
              </div>
            </div>
            <p className="text-gray-500 text-sm">
              {door === 'ideas'
                ? <>Fresh plays for {tradePlural} — run them in order.</>
                : <>Run these plays in order — built for how {tradePlural} get found.</>}
            </p>

            {pb ? (
              <PlaybookPlays pb={pb} trade={tradePlural} door={door} />
            ) : (
              <div className="mt-5 rounded-xl bg-amber-50 ring-1 ring-amber-200 text-amber-800 text-sm px-4 py-3">
                We're putting your playbook together. Give it a moment and refresh — or finish setup and it'll be ready inside.
              </div>
            )}

            <div className="mt-8 text-center">
              <a
                href="/?modal=signup"
                className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-white text-lg font-semibold transition-transform hover:scale-[1.03] shadow-lg"
                style={{ background: BRAND }}
              >
                {door === 'ideas' ? 'Get fresh ideas + publish →' : 'Finish & publish this →'}
              </a>
              <div className="mt-3 text-sm text-gray-500">we design it, you hit publish — about 5 minutes</div>
              <EmailMyPlaybook
                profession={displayTrade}
                professionCanonicalId={captured && captured.professionCanonicalId}
                captureToken={captured && captured.captureToken}
              />
              <SharePlaybook trade={displayTrade} tradePlural={tradePlural} />
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
